import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronDown, ChevronRight, User, Phone, Mail, MapPin, 
  Calendar, FileText, DollarSign, Shield, Clock, Plus,
  AlertCircle, CheckCircle, XCircle, Edit, Eye, Printer
} from 'lucide-react';
import { getAuthToken } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-lg mb-3 bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-t-lg text-left"
      >
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
          {Icon && <Icon className="w-4 h-4 text-teal-600" />}
          <span className="font-semibold text-gray-700">{title}</span>
          {badge && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded-full">{badge}</span>
          )}
        </div>
      </button>
      {isOpen && <div className="p-4 border-t border-gray-200">{children}</div>}
    </div>
  );
};

// Data Row Component
const DataRow = ({ label, value, className = "" }) => (
  <div className={`flex ${className}`}>
    <span className="text-gray-500 text-sm w-32 flex-shrink-0">{label}:</span>
    <span className="text-gray-900 text-sm font-medium">{value || 'N/A'}</span>
  </div>
);

// Balance Box Component
const BalanceBox = ({ label, amount, type = "neutral" }) => {
  const colorClasses = {
    positive: "bg-green-50 border-green-200 text-green-700",
    negative: "bg-red-50 border-red-200 text-red-700",
    neutral: "bg-gray-50 border-gray-200 text-gray-700"
  };
  
  return (
    <div className={`p-3 border rounded-lg text-center ${colorClasses[type]}`}>
      <div className="text-xs uppercase tracking-wide mb-1">{label}</div>
      <div className="text-lg font-bold">${parseFloat(amount || 0).toFixed(2)}</div>
    </div>
  );
};

// Aging Bucket Component
const AgingBucket = ({ label, amount }) => (
  <div className="text-center px-2">
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-sm font-semibold ${parseFloat(amount) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
      ${parseFloat(amount || 0).toFixed(2)}
    </div>
  </div>
);

const PatientDashboardPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [insurance, setInsurance] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPatientData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch patient chart (comprehensive data)
      const chartRes = await fetch(`${API}/api/patients/${patientId}/chart`, { headers });
      if (!chartRes.ok) throw new Error('Failed to fetch patient data');
      const chart = await chartRes.json();
      
      setPatient(chart.patient);
      setChartData(chart);

      // Fetch insurance
      const insRes = await fetch(`${API}/api/insurance/patient/${patientId}`, { headers });
      if (insRes.ok) {
        const insData = await insRes.json();
        setInsurance(insData);
      }

      // Fetch invoices
      const invRes = await fetch(`${API}/api/billing/invoices`, { headers });
      if (invRes.ok) {
        const invData = await invRes.json();
        setInvoices(invData.filter(inv => inv.patient_id === patientId));
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: '2-digit', day: '2-digit', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', { 
        month: '2-digit', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} years`;
  };

  const calculateBalances = () => {
    let insuranceBalance = 0;
    let patientBalance = 0;
    let aging = { '0-30': 0, '31-60': 0, '61-90': 0, '91-120': 0, '121+': 0 };
    
    invoices.forEach(inv => {
      const balance = parseFloat(inv.balance_due || 0);
      patientBalance += balance;
      
      // Calculate aging
      const dueDate = new Date(inv.due_date);
      const today = new Date();
      const daysPast = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      
      if (daysPast <= 30) aging['0-30'] += balance;
      else if (daysPast <= 60) aging['31-60'] += balance;
      else if (daysPast <= 90) aging['61-90'] += balance;
      else if (daysPast <= 120) aging['91-120'] += balance;
      else aging['121+'] += balance;
    });

    return { insuranceBalance, patientBalance, aging };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-600">Error: {error || 'Patient not found'}</div>
      </div>
    );
  }

  const balances = calculateBalances();
  const encounters = chartData?.encounters || [];
  const appointments = chartData?.appointments || [];
  const documents = chartData?.documents || [];
  const consents = chartData?.hipaa_consents || [];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="text-white/80 hover:text-white"
            >
              ← Back
            </button>
            <div>
              <h1 className="text-2xl font-bold">Patient Dashboard</h1>
              <p className="text-teal-100 text-sm">Comprehensive Patient View</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold">Patient: {patient.id?.slice(0, 8)}</div>
          </div>
        </div>
      </div>

      {/* Patient Name Banner */}
      <div className="bg-white border-b px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
            <User className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {patient.last_name?.toUpperCase()}, {patient.first_name?.toUpperCase()}
            </h2>
            <p className="text-gray-500 text-sm">
              DOB: {formatDate(patient.date_of_birth)} | {calculateAge(patient.date_of_birth)} | {patient.gender || 'N/A'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2">
            <Edit className="w-4 h-4" /> Edit
          </button>
          <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Demographics & Insurance */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Demographics Section */}
            <CollapsibleSection title="Demographics" icon={User} defaultOpen={true}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <DataRow label="Full Name" value={`${patient.first_name} ${patient.last_name}`} />
                  <DataRow label="Address" value={patient.address || 'Not provided'} />
                  <DataRow label="City, State ZIP" value={
                    patient.city ? `${patient.city}, ${patient.state || ''} ${patient.zip || ''}` : 'Not provided'
                  } />
                </div>
                <div className="space-y-2">
                  <DataRow label="Home Phone" value={patient.phone} />
                  <DataRow label="Cell Phone" value={patient.cell_phone} />
                  <DataRow label="Email" value={patient.email} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DataRow label="Emergency Contact" value={patient.emergency_contact_name} />
                  <DataRow label="Emergency Phone" value={patient.emergency_contact_phone} />
                </div>
              </div>
            </CollapsibleSection>

            {/* Guarantor Section */}
            <CollapsibleSection title="Guarantors" icon={User} badge={1} defaultOpen={true}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Name/Employer</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">DOB</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Contact Info</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Relationship</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-blue-600 underline cursor-pointer">
                          {patient.last_name}, {patient.first_name}
                        </div>
                        <div className="text-xs text-gray-500">{patient.address || 'No address'}</div>
                      </td>
                      <td className="px-3 py-2">
                        {formatDate(patient.date_of_birth)}<br />
                        <span className="text-xs text-gray-500">{calculateAge(patient.date_of_birth)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">H: {patient.phone || 'N/A'}</div>
                        <div className="text-xs">C: {patient.cell_phone || 'N/A'}</div>
                        <div className="text-xs">E: {patient.email || 'N/A'}</div>
                      </td>
                      <td className="px-3 py-2">Self</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        ${balances.patientBalance.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <span className="text-gray-500">Paperless:</span>
                  {patient.paperless ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-gray-500">Email Notification:</span>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-gray-500">Text Notification:</span>
                  <XCircle className="w-4 h-4 text-red-500" />
                </label>
              </div>
            </CollapsibleSection>

            {/* Balance Section */}
            <CollapsibleSection title="Balance" icon={DollarSign} defaultOpen={true}>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <BalanceBox label="Insurance" amount={balances.insuranceBalance} />
                <BalanceBox label="Patient" amount={balances.patientBalance} type={balances.patientBalance > 0 ? "negative" : "neutral"} />
                <BalanceBox label="Collection" amount={0} />
                <BalanceBox label="Escrow" amount={0} />
              </div>
              
              {/* Aging Buckets */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-700 mb-3">Responsible Party Aging</div>
                <div className="flex justify-between border-t pt-3">
                  <AgingBucket label="0-30" amount={balances.aging['0-30']} />
                  <AgingBucket label="31-60" amount={balances.aging['31-60']} />
                  <AgingBucket label="61-90" amount={balances.aging['61-90']} />
                  <AgingBucket label="91-120" amount={balances.aging['91-120']} />
                  <AgingBucket label="121+" amount={balances.aging['121+']} />
                  <AgingBucket label="Total" amount={balances.patientBalance} />
                </div>
              </div>
            </CollapsibleSection>

            {/* Encounters Section */}
            <CollapsibleSection title="Encounters" icon={FileText} badge={encounters.length} defaultOpen={true}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Encounter</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">DOS</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Provider</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {encounters.length === 0 ? (
                      <tr><td colSpan="6" className="px-3 py-4 text-center text-gray-500">No encounters found</td></tr>
                    ) : encounters.slice(0, 10).map((enc, idx) => (
                      <tr key={enc.id || idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-blue-600">{enc.id?.slice(0, 8)}</td>
                        <td className="px-3 py-2">{formatDate(enc.created_at)}</td>
                        <td className="px-3 py-2">{enc.encounter_type?.replace('_', ' ').toUpperCase()}</td>
                        <td className="px-3 py-2">{enc.provider_name || 'Unassigned'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            enc.status === 'completed' ? 'bg-green-100 text-green-700' :
                            enc.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {enc.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button className="text-teal-600 hover:text-teal-800">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>

            {/* Active Insurance Policies */}
            <CollapsibleSection title="Active Policies / Insurance" icon={Shield} badge={insurance.length} defaultOpen={true}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Insurance</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Role</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Policy #</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Group #</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Relationship</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Policy Holder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insurance.length === 0 ? (
                      <tr><td colSpan="7" className="px-3 py-4 text-center text-gray-500">No insurance on file</td></tr>
                    ) : insurance.map((ins, idx) => (
                      <tr key={ins.id || idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{ins.provider_name}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            ins.verification_status === 'verified' ? 'bg-green-100 text-green-700' :
                            ins.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {ins.verification_status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className="px-3 py-2">{ins.is_primary ? 'PRIMARY' : 'SECONDARY'}</td>
                        <td className="px-3 py-2">{ins.policy_number}</td>
                        <td className="px-3 py-2">{ins.group_number || 'N/A'}</td>
                        <td className="px-3 py-2">{ins.relationship?.toUpperCase()}</td>
                        <td className="px-3 py-2">{ins.subscriber_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3">
                <button className="text-sm text-teal-600 hover:text-teal-800 flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Add Insurance
                </button>
              </div>
            </CollapsibleSection>
          </div>

          {/* Right Column - Appointments, Documents, Notes */}
          <div className="space-y-4">
            
            {/* Patient Appointments */}
            <CollapsibleSection title="Patient Appointments" icon={Calendar} badge={appointments.length} defaultOpen={true}>
              <div className="space-y-2">
                {appointments.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No appointments scheduled</p>
                ) : appointments.slice(0, 5).map((apt, idx) => (
                  <div key={apt.id || idx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-teal-500">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm">{formatDate(apt.scheduled_time)}</div>
                        <div className="text-xs text-gray-500">{apt.appointment_type?.replace('_', ' ')}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        apt.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                        apt.status === 'completed' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {apt.status}
                      </span>
                    </div>
                    {apt.reason && <div className="text-xs text-gray-600 mt-1">{apt.reason}</div>}
                  </div>
                ))}
              </div>
              <button className="mt-3 w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-teal-500 hover:text-teal-600">
                + Schedule Appointment
              </button>
            </CollapsibleSection>

            {/* Patient Documents */}
            <CollapsibleSection title="Patient Documents" icon={FileText} badge={documents.length} defaultOpen={true}>
              <div className="space-y-2">
                {documents.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No documents uploaded</p>
                ) : documents.slice(0, 5).map((doc, idx) => (
                  <div key={doc.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">{doc.document_type?.replace('_', ' ')}</div>
                        <div className="text-xs text-gray-500">{formatDate(doc.uploaded_at)}</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button className="p-1 hover:bg-gray-200 rounded" title="View">
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      <button className="p-1 hover:bg-gray-200 rounded" title="Print">
                        <Printer className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* HIPAA Consents */}
            <CollapsibleSection title="HIPAA Consents" icon={Shield} badge={consents.length} defaultOpen={false}>
              <div className="space-y-2">
                {consents.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No consents signed</p>
                ) : consents.map((consent, idx) => (
                  <div key={consent.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="text-sm font-medium">{consent.consent_type?.replace('_', ' ')}</div>
                      <div className="text-xs text-gray-500">Signed: {formatDate(consent.signature_date)}</div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Quick Notes */}
            <CollapsibleSection title="Notes" icon={FileText} defaultOpen={false}>
              <textarea 
                className="w-full p-3 border rounded-lg text-sm resize-none" 
                rows={4} 
                placeholder="Add a note about this patient..."
              />
              <button className="mt-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">
                Save Note
              </button>
            </CollapsibleSection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboardPage;
